import { Button, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { getPendingBindingWork } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentBindingCard.utils';
import { useDocumentBindingStatus } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/useDocumentBindingStatus';
import {
	BindingCard,
	CardText,
	IconTile,
	StatusLine,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentsModal/components/documentBindingCard.styled';

interface Props {
	templateId: string;
	isDocx: boolean;
	showFieldsTab: boolean;
	canPlaceSignaturesManually: boolean;
	postDatingEnabled: boolean;
	disabled: boolean;
	onOpen: () => void;
}

const PENDING_KEY = {
	both: 'send_documents.binding.pending.both',
	fields: 'send_documents.binding.pending.fields',
	signatures: 'send_documents.binding.pending.signatures',
	none: 'send_documents.binding.pending.none',
} as const;

/**
 * The row editor's entry point to the document editor. Says what is still unplaced before the
 * author opens anything, so a document that needs no work reads as done at a glance.
 */
export const DocumentBindingCard = ({
	templateId,
	isDocx,
	showFieldsTab,
	canPlaceSignaturesManually,
	postDatingEnabled,
	disabled,
	onOpen,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { status, isLoading } = useDocumentBindingStatus({
		templateId,
		isDocx,
		showFieldsTab,
		canPlaceSignaturesManually,
		postDatingEnabled,
		enabled: true,
	});

	// A document with no data points at all says nothing about bindings rather than "0 fields
	// bound", which reads as a problem when there is simply nothing to bind.
	const boundCount = status.fields && status.fields.total > 0 ? status.fields.done : undefined;
	const pendingText = tMipManage(PENDING_KEY[getPendingBindingWork(status)]);
	const boundText =
		boundCount === undefined
			? undefined
			: tMipManage(
					boundCount === 1
						? 'send_documents.binding.bound_one'
						: 'send_documents.binding.bound_other',
					{ count: boundCount }
				);

	const subtitle = isLoading
		? tMipManage('send_documents.binding.checking')
		: [pendingText, boundText].filter(Boolean).join(' - ');

	return (
		<BindingCard data-testid={`send-documents-binding-card-${templateId}`}>
			<IconTile>
				<Icon variant="bind-fields" size="xl" />
			</IconTile>
			<CardText>
				<Text fz="sm" fw={600} c="dark.8" lh="1.25rem">
					{tMipManage('send_documents.binding.title')}
				</Text>
				<StatusLine>{subtitle}</StatusLine>
			</CardText>
			<Button
				data-testid="send-documents-open-document-editor"
				variant="outline"
				disabled={disabled}
				onClick={onOpen}
			>
				{tMipManage('send_documents.binding.open_editor')}
			</Button>
		</BindingCard>
	);
};
